import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { authService } from "@/lib/supabase";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export const Route = createFileRoute("/auth-callback")({
  component: AuthCallbackPage,
});

function AuthCallbackPage() {
  const navigate = useNavigate();
  const { authentication, queryClient } = Route.useRouteContext();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    handleAuthCallback();
  }, []);

  const handleAuthCallback = async () => {
    try {
      // Get authorization code from URL
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");
      const error = urlParams.get("error");
      const errorDescription = urlParams.get("error_description");

      if (error) {
        throw new Error(errorDescription || error);
      }

      if (!code) {
        throw new Error("No authorization code received");
      }

      // Exchange code for session
      const result = await authService.handleOAuthCallback(code);

      if (result.error) {
        throw result.error;
      }

      if (result.session && result.user && authentication) {
        // Update authentication context
        authentication.setUser(result.user);

        // Invalidate queries
        queryClient.invalidateQueries({ queryKey: ["authentication"] });

        // Show success message
        toast.success("Successfully signed in with Google");

        // Redirect to appropriate page
        if (result.user.currentProjectId) {
          navigate({
            to: "/$projectId",
            params: { projectId: result.user.currentProjectId },
            replace: true,
          });
        } else {
          navigate({ to: "/project-create-or-join", replace: true });
        }
      } else {
        throw new Error("Authentication failed");
      }
    } catch (err) {
      console.error("Auth callback error:", err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to complete authentication";
      setError(errorMessage);
      toast.error(errorMessage);

      // Redirect to login page after a delay
      setTimeout(() => {
        navigate({ to: "/login", replace: true });
      }, 3000);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Authentication</CardTitle>
          <CardDescription>
            {isProcessing
              ? "Processing your authentication..."
              : error
                ? "Authentication failed"
                : "Authentication complete"}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center space-y-4">
          {isProcessing ? (
            <>
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">
                Please wait while we complete your authentication...
              </p>
            </>
          ) : error ? (
            <>
              <div className="text-red-500 text-center">
                <p className="font-semibold">Authentication failed</p>
                <p className="text-sm mt-2">{error}</p>
              </div>
              <p className="text-sm text-muted-foreground">
                Redirecting to login page...
              </p>
            </>
          ) : (
            <>
              <div className="text-green-500 text-center">
                <p className="font-semibold">Successfully authenticated!</p>
              </div>
              <p className="text-sm text-muted-foreground">
                Redirecting to your project...
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

